// src/features/data/orphans-tab.tsx
import { Chip, DataTable, Kicker } from "@/components/iris";
import type { DataTableColumn } from "@/components/iris";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import { useDeleteGroup, useOrphanGroups } from "@/hooks/use-data";
import { useTriggerRescrape } from "@/hooks/use-pipeline";
import type { OrphanGroupRecord } from "@/lib/types";
import { AlertTriangle, RefreshCw, RotateCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { useClientSort } from "./use-client-sort";

// ─── Pure Helpers (exported for tests) ───

export type AgeBucketKey = "le7d" | "8to30d" | "31to90d" | "gt90d";

export interface AgeBucketCounts {
  le7d: number;
  "8to30d": number;
  "31to90d": number;
  gt90d: number;
}

export const AGE_BUCKET_LABELS: Record<AgeBucketKey, string> = {
  le7d: "≤7d",
  "8to30d": "8–30d",
  "31to90d": "31–90d",
  gt90d: ">90d",
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function bucketByAge(items: OrphanGroupRecord[], now: Date): AgeBucketCounts {
  const counts: AgeBucketCounts = { le7d: 0, "8to30d": 0, "31to90d": 0, gt90d: 0 };
  const nowMs = now.getTime();
  for (const item of items) {
    if (!item.created_at) continue;
    const createdMs = new Date(item.created_at).getTime();
    if (Number.isNaN(createdMs)) continue;
    const ageDays = (nowMs - createdMs) / DAY_MS;
    if (ageDays <= 7) counts.le7d += 1;
    else if (ageDays <= 30) counts["8to30d"] += 1;
    else if (ageDays <= 90) counts["31to90d"] += 1;
    else counts.gt90d += 1;
  }
  return counts;
}

export function platformOf(item: OrphanGroupRecord): string {
  if (item.platform && item.platform.length > 0) return item.platform;
  if (item.app_type && item.app_type.length > 0 && item.app_type[0]) return item.app_type[0];
  return "unknown";
}

export function aggregateByPlatform(items: OrphanGroupRecord[]): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const item of items) {
    const key = platformOf(item);
    acc[key] = (acc[key] ?? 0) + 1;
  }
  return acc;
}

const DATA_STATE_LABELS: Record<string, string> = {
  awaiting_scrape: "Awaiting scrape",
  all_past_members: "Empty (no live members)",
  empty_scrape: "Empty scrape",
};

function dataStateLabel(state: string): string {
  return DATA_STATE_LABELS[state] ?? state;
}

function fmtAbsolute(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtRelative(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = now - d.getTime();
  if (diffMs < 0) return "just now";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function pct(part: number, total: number): string {
  if (!total) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

// ─── KPI Row ───

function KpiRow({
  total,
  awaiting,
  other,
}: {
  total: number;
  awaiting: number;
  other: number;
}) {
  const items = [
    { label: "Total Orphans", value: total },
    { label: "Awaiting Scrape", value: awaiting },
    { label: "Other States", value: other },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
      {items.map(({ label, value }) => (
        <div key={label} className="iris-card" style={{ padding: 16 }}>
          <Kicker variant="muted">{label}</Kicker>
          <div className="iris-kpi" style={{ marginTop: 4, fontSize: 24 }}>
            {value.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Age Buckets Card ───

function AgeBucketsCard({ items }: { items: OrphanGroupRecord[] }) {
  const counts = bucketByAge(items, new Date());
  const total = items.length;
  const order: AgeBucketKey[] = ["le7d", "8to30d", "31to90d", "gt90d"];
  return (
    <div className="iris-card" style={{ padding: 16 }}>
      <Kicker variant="muted" style={{ marginBottom: 8 }}>
        Age (since created)
      </Kicker>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {order.map((key) => (
          <div key={key}>
            <div style={{ fontSize: 11, color: "var(--iris-fg-4)" }}>{AGE_BUCKET_LABELS[key]}</div>
            <div className="iris-kpi" style={{ marginTop: 2, fontSize: 20 }}>
              {counts[key].toLocaleString()}
            </div>
            <div style={{ fontSize: 11, color: "var(--iris-fg-4)" }}>{pct(counts[key], total)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Per-platform Card ───

function PlatformBreakdownCard({ items }: { items: OrphanGroupRecord[] }) {
  const counts = aggregateByPlatform(items);
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (
    <div className="iris-card" style={{ padding: 16 }}>
      <Kicker variant="muted" style={{ marginBottom: 8 }}>
        Per platform
      </Kicker>
      {entries.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--iris-fg-4)" }}>{"—"}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {entries.map(([platform, count]) => (
            <div
              key={platform}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: 12,
              }}
            >
              <Chip style={{ fontSize: 10, padding: "2px 8px" }}>{platform}</Chip>
              <span style={{ color: "var(--iris-fg-3)" }}>{count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main ───

export function OrphansTab() {
  const { data, isLoading, isFetching, error, refetch } = useOrphanGroups();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const awaiting = data?.by_state?.awaiting_scrape ?? 0;
  const other = Math.max(0, total - awaiting);

  // IR-1045 Phase 1: client-side multi-select filters (data is the full
  // loaded orphan list, not server-paginated).
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState<string[]>([]);

  const platformOptions = [...new Set(items.map(platformOf))]
    .sort()
    .map((p) => ({ value: p, label: p }));
  const stateOptions = [...new Set(items.map((g) => g.data_state))]
    .sort()
    .map((s) => ({ value: s, label: dataStateLabel(s) }));

  const filtered = items.filter(
    (g) =>
      (platformFilter.length === 0 || platformFilter.includes(platformOf(g))) &&
      (stateFilter.length === 0 || stateFilter.includes(g.data_state)),
  );

  // IR-1045 Phase 2: row selection + Delete/Re-scrape action bar (single or
  // multi). Orphans are client-side, so select-all spans the filtered list.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const deleteGroup = useDeleteGroup();
  const rescrape = useTriggerRescrape();
  const [deleting, setDeleting] = useState(false);

  const toggleSelectId = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected = filtered.length > 0 && filtered.every((g) => selectedIds.has(g.id));
  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (filtered.every((g) => next.has(g.id))) {
        for (const g of filtered) next.delete(g.id);
      } else {
        for (const g of filtered) next.add(g.id);
      }
      return next;
    });

  const selectedRows = filtered.filter((g) => selectedIds.has(g.id));

  function handleRescrapeSelected() {
    if (selectedRows.length === 0) return;
    if (!confirm(`Re-scrape ${selectedRows.length} group(s)?`)) return;
    for (const g of selectedRows) rescrape.mutate(g.scrapping_id);
    setSelectedIds(new Set());
  }

  async function handleDeleteSelected() {
    if (selectedRows.length === 0) return;
    if (
      !confirm(
        `Delete ${selectedRows.length} orphan group(s) from BOTH environments? This removes the group records from the DB.`,
      )
    )
      return;
    setDeleting(true);
    try {
      for (const g of selectedRows) {
        await deleteGroup.mutateAsync({ id: g.encrypted_id, targetEnv: "both" });
      }
    } finally {
      setDeleting(false);
      setSelectedIds(new Set());
      refetch();
    }
  }

  const columns: DataTableColumn<OrphanGroupRecord>[] = [
    {
      // IR-1045 Phase 2: per-row + select-all (over the filtered list) checkbox.
      key: "__select",
      label: (
        <input
          type="checkbox"
          aria-label="Select all"
          checked={allSelected}
          onChange={toggleSelectAll}
          style={{ accentColor: "var(--iris-neon-purple)" }}
        />
      ) as unknown as string,
      render: (g) => (
        <input
          type="checkbox"
          aria-label={`Select ${g.name}`}
          checked={selectedIds.has(g.id)}
          onChange={() => toggleSelectId(g.id)}
          style={{ accentColor: "var(--iris-neon-purple)" }}
        />
      ),
    },
    {
      key: "id",
      label: "ID",
      sortable: true,
      cellVariant: "mono",
      render: (g) => <span style={{ color: "var(--iris-fg-3)" }}>{g.id}</span>,
    },
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (g) => (
        <span
          style={{
            color: "var(--iris-fg-1)",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-block",
            maxWidth: 280,
          }}
          title={g.name}
        >
          {g.name}
        </span>
      ),
    },
    {
      key: "platform",
      label: "Platform",
      sortable: true,
      render: (g) => <Chip style={{ fontSize: 10, padding: "2px 8px" }}>{platformOf(g)}</Chip>,
    },
    {
      key: "data_state",
      label: "State",
      sortable: true,
      render: (g) => (
        <span style={{ color: "var(--iris-fg-3)" }}>{dataStateLabel(g.data_state)}</span>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      sortable: true,
      render: (g) => (
        <span style={{ color: "var(--iris-fg-3)" }} title={fmtAbsolute(g.created_at)}>
          {fmtRelative(g.created_at)}
        </span>
      ),
    },
    {
      key: "last_scrape_time",
      label: "Last scrape",
      sortable: true,
      render: (g) =>
        g.last_scrape_time ? (
          <span style={{ color: "var(--iris-fg-3)" }} title={fmtAbsolute(g.last_scrape_time)}>
            {fmtRelative(g.last_scrape_time)}
          </span>
        ) : (
          <span style={{ color: "var(--iris-fg-4)" }}>{"—"}</span>
        ),
    },
    {
      key: "member_row_count",
      label: "Members",
      sortable: true,
      headerAlign: "right",
      cellVariant: "num",
      render: (g) => <span>{g.member_row_count.toLocaleString()}</span>,
    },
  ];

  // IR-1045 Phase 1: wire real per-column ASC/DESC sort (previously the
  // headers were marked sortable but onSort was never passed — clicking did
  // nothing). Default remains created_at asc (oldest first — most overdue).
  const toMs = (iso: string | null) => (iso ? new Date(iso).getTime() : null);
  const { sorted, sortKey, sortDirection, onSort } = useClientSort<OrphanGroupRecord>(
    filtered,
    {
      id: (g) => g.id,
      name: (g) => g.name,
      platform: (g) => platformOf(g),
      data_state: (g) => g.data_state,
      created_at: (g) => toMs(g.created_at),
      last_scrape_time: (g) => toMs(g.last_scrape_time),
      member_row_count: (g) => g.member_row_count,
    },
    { key: "created_at", direction: "asc" },
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <Kicker variant="magenta">Orphan Groups</Kicker>
          <p className="iris-meta" style={{ marginTop: 4 }}>
            Hidden groups (awaiting scrape or empty after scrape) {"—"} excluded from public views.
          </p>
        </div>
        <button
          type="button"
          className="iris-btn"
          onClick={() => refetch()}
          disabled={isFetching}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <RefreshCw size={12} className={isFetching ? "iris-pulse" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            borderRadius: "var(--iris-r-lg)",
            border: "1px solid rgba(255,77,210,0.2)",
            background: "rgba(255,77,210,0.06)",
            padding: "12px 16px",
            color: "var(--iris-neon-pink)",
            fontSize: 12,
          }}
        >
          <AlertTriangle size={14} />
          Failed to load orphan groups: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {isLoading && !data && (
        <div className="iris-card" style={{ padding: "24px 18px" }}>
          <div style={{ color: "var(--iris-fg-3)", fontSize: 12 }}>Loading orphan groups...</div>
        </div>
      )}

      {data && (
        <>
          {/* KPI cards */}
          <KpiRow total={total} awaiting={awaiting} other={other} />

          {/* Breakdown cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <AgeBucketsCard items={items} />
            <PlatformBreakdownCard items={items} />
          </div>

          {/* Filter bar */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <MultiSelectFilter
              label="Platform"
              options={platformOptions}
              selected={platformFilter}
              onChange={setPlatformFilter}
            />
            <MultiSelectFilter
              label="State"
              options={stateOptions}
              selected={stateFilter}
              onChange={setStateFilter}
            />
            <span className="iris-meta" style={{ marginLeft: "auto" }}>
              {filtered.length.toLocaleString()}
              {filtered.length !== items.length ? ` of ${items.length.toLocaleString()}` : ""} shown
            </span>
          </div>

          {/* IR-1045 Phase 2: action bar (above the table) for selected rows. */}
          {selectedIds.size > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                borderRadius: "var(--iris-r-lg)",
                border: "1px solid color-mix(in srgb, var(--iris-neon-purple) 30%, transparent)",
                background: "color-mix(in srgb, var(--iris-neon-purple) 5%, transparent)",
                padding: "8px 16px",
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--iris-neon-purple)" }}>
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                className="iris-btn"
                onClick={handleRescrapeSelected}
                disabled={rescrape.isPending}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}
              >
                <RotateCw size={12} /> Re-scrape
              </button>
              <button
                type="button"
                className="iris-btn"
                onClick={handleDeleteSelected}
                disabled={deleting}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "var(--iris-neon-pink)",
                  borderColor: "color-mix(in srgb, var(--iris-neon-pink) 40%, transparent)",
                }}
              >
                <Trash2 size={12} /> {deleting ? "Deleting…" : "Delete"}
              </button>
              <button
                type="button"
                className="iris-btn"
                onClick={() => setSelectedIds(new Set())}
                style={{ marginLeft: "auto", fontSize: 12 }}
              >
                Clear
              </button>
            </div>
          )}

          {/* Table */}
          <div className="iris-card" style={{ padding: 0, overflow: "hidden" }}>
            {sorted.length === 0 ? (
              <div style={{ padding: "16px 18px", color: "var(--iris-fg-3)", fontSize: 12 }}>
                No orphan groups found.
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={sorted}
                rowKey={(g) => g.id}
                onSort={onSort}
                sortKey={sortKey}
                sortDirection={sortDirection}
                emptyMessage="No orphan groups found"
              />
            )}
          </div>

          {data.page_limit && total > data.page_limit && (
            <div style={{ fontSize: 11, color: "var(--iris-fg-4)" }}>
              Showing first {data.page_limit.toLocaleString()} of {total.toLocaleString()} (page
              limit).
            </div>
          )}
        </>
      )}
    </div>
  );
}
